import * as React from 'react';

import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DataTable } from '@/components/data-table/data-table';
import {
  DefaultTableErrorState,
  DefaultTableLoadingState,
} from '@/components/data-table/data-table-states';
import { useColumnSizing } from '@/hooks/use-column-sizing';
import { TableIds } from '@/lib/constants/table-ids';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { SettingsRoutes } from '@/router/routes';

import { getTimeOffTypesColumns } from '../../constants/time-off-types-table-columns';
import { TimeOffTypeListItem } from '../../types/time-off';

interface TimeOffTypesTableProps {
  data: TimeOffTypeListItem[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
}

export const TimeOffTypesTable: React.FC<TimeOffTypesTableProps> = ({
  data,
  isLoading,
  error,
  onRetry,
}) => {
  const { t } = useTranslation(['authenticated.settings', 'common']);
  const navigate = useNavigate();
  const { getErrorMessage } = useApiError();

  const {
    columnSizing,
    columnSizingInfo,
    onColumnSizingChange,
    onColumnSizingInfoChange,
    saveColumnSizing,
  } = useColumnSizing(TableIds.TIME_OFF_TYPES);

  const columns = React.useMemo(
    () => getTimeOffTypesColumns({ t, navigate }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const buildRowHref = React.useCallback(
    (row: TimeOffTypeListItem) => SettingsRoutes.TimeOffDetails.replace(':typeId', String(row.id)),
    []
  );

  const handleRowClick = React.useCallback(
    (row: TimeOffTypeListItem) => {
      navigate(buildRowHref(row));
    },
    [navigate, buildRowHref]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    state: { columnSizing, columnSizingInfo },
    onColumnSizingChange,
    onColumnSizingInfoChange,
  });

  const colSpan = table.getAllColumns().length;

  const lastRowChildren = (
    <>
      {isLoading && <DefaultTableLoadingState colSpan={colSpan} />}
      {error && !isLoading && (
        <DefaultTableErrorState
          colSpan={colSpan}
          error={getErrorMessage(error)}
          retryLoad={onRetry}
        />
      )}
      {!isLoading && !error && data.length === 0 && (
        <tr>
          <td colSpan={colSpan} className="h-16 px-12">
            <div className="text-sm">{t('common:noResultsFound')}</div>
          </td>
        </tr>
      )}
      {/* Add time-off type button — uncomment when backend is ready
      <tr className="hover:bg-transparent">
        <td colSpan={colSpan} className="h-16 px-4">
          <div className="flex justify-start">
            <Button type="button" variant="ghost" size="sm" className="gap-2">
              <CirclePlus className="h-4 w-4" />
              {t('pages.timeOff.addTimeOffType')}
            </Button>
          </div>
        </td>
      </tr>
      */}
    </>
  );

  return (
    <DataTable
      table={table}
      lastRowChildren={lastRowChildren}
      tableId={TableIds.TIME_OFF_TYPES}
      onResizeEnd={saveColumnSizing}
      onRowClick={handleRowClick}
      getRowHref={buildRowHref}
    />
  );
};
